import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, TrendingUp, Award, Users, Target, Quote, CheckCircle, BarChart3, Zap, AlertCircle, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import TestimonialWall from '../components/TestimonialWall';
import SEOHead from '../components/SEOHead';
import { scrollToSection } from '../utils/smoothScroll';
import { apiFetch } from '../lib/apiClient';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Custom hook for scroll animations
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// Animated Section Component
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  
  return (
    <div 
      ref={ref}
      className={`transition-all duration-700 ease-out ${className} ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-12'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const fallbackResults = [
  {
    metric: '+180%',
    title: 'Crescita fatturato',
    description: 'Azienda B2B servizi. Da €200K a €560K in 18 mesi.',
  },
  {
    metric: '€850K',
    title: 'Nuovo mercato',
    description: 'Lancio internazionale. ROI 4x sull\'investimento marketing.',
  },
  {
    metric: '-45%',
    title: 'Costo acquisizione',
    description: 'E-commerce fashion. CAC ridotto da €38 a €21.',
  },
];

const fallbackTestimonials = [
  {
    name: 'Marco Bianchi',
    role: 'CEO',
    company: 'TechFlow Solutions',
    text: 'Nicola ha trasformato completamente il nostro approccio al marketing. In 12 mesi abbiamo triplicato i lead qualificati e il fatturato è cresciuto del 180%.',
    result: '+180% fatturato'
  },
  {
    name: 'Laura Verdi',
    role: 'Founder',
    company: 'Design Studio Milano',
    text: 'Finalmente qualcuno che parla di strategia e non solo di tattiche. Il metodo D.O.M.I.N.A. ci ha dato una direzione chiara e risultati concreti.',
    result: '3x lead qualificati'
  },
  {
    name: 'Giuseppe Rossi',
    role: 'Direttore Marketing',
    company: 'Industrie Rossi SpA',
    text: 'Avevamo provato 3 agenzie prima di Nicola. La differenza? Lui capisce il business, non solo il marketing. Risultati in 90 giorni.',
    result: '-45% CAC'
  },
];

const RisultatiPage = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState(fallbackResults);
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resultsRes, testimonialsRes] = await Promise.all([
          apiFetch(`${API_URL}/api/results`),
          apiFetch(`${API_URL}/api/testimonials`)
        ]);
        
        if (resultsRes.ok) {
          const data = await resultsRes.json();
          if (data.length > 0) setResults(data);
        }
        
        if (testimonialsRes.ok) {
          const data = await testimonialsRes.json();
          if (data.length > 0) setTestimonials(data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const scrollToContact = () => {
    scrollToSection('contact');
  };

  const caseStudies = [
    {
      title: 'Da €200K a €560K in 18 mesi',
      category: 'B2B Services',
      challenge: 'Azienda di consulenza IT con buon servizio ma zero strategia marketing. Clienti arrivavano solo per passaparola.',
      solution: 'Implementato il metodo D.O.M.I.N.A. completo: riposizionamento, content strategy, LinkedIn marketing, funnel automatizzato.',
      results: [
        { value: '+180%', label: 'fatturato' },
        { value: '3x', label: 'lead mensili' },
        { value: '+25%', label: 'margini' },
        { value: '4→12', label: 'team' }
      ],
      timeline: '18 mesi'
    },
    {
      title: 'Lancio internazionale con ROI 4x',
      category: 'SaaS',
      challenge: 'Software italiano pronto per l\'espansione europea ma senza strategia go-to-market.',
      solution: 'Analisi mercati target, adattamento messaging, campagne paid multi-country, partnership strategiche.',
      results: [
        { value: '€850K', label: 'nuovo revenue' },
        { value: '4x', label: 'ROI marketing' },
        { value: '3', label: 'nuovi mercati' },
        { value: '+200%', label: 'MRR' }
      ],
      timeline: '12 mesi'
    },
    {
      title: 'CAC ridotto del 45%',
      category: 'E-commerce',
      challenge: 'E-commerce fashion con CAC insostenibile. Ogni nuovo cliente costava €38, margine quasi azzerato.',
      solution: 'Ottimizzazione funnel, segmentazione audience, creatività data-driven, email marketing automation.',
      results: [
        { value: '-45%', label: 'CAC' },
        { value: '+60%', label: 'ROAS' },
        { value: '+35%', label: 'LTV' },
        { value: '+18%', label: 'margine' }
      ],
      timeline: '6 mesi'
    },
  ];

  const stats = [
    { value: '14+', label: 'Anni di esperienza', icon: Target },
    { value: '€2.5M+', label: 'Revenue generata', icon: TrendingUp },
    { value: '4', label: 'Clienti alla volta', icon: Users },
    { value: '100%', label: 'Personalizzato', icon: Award },
  ];

  const philosophy = [
    {
      title: "Investimento, non costo",
      quote: "Il marketing non è un costo. È l'investimento con il ROI più alto che puoi fare, se sai cosa stai facendo.",
      icon: BarChart3
    },
    {
      title: "Dati, non opinioni",
      quote: "Ogni decisione deve essere basata su numeri. Le sensazioni sono utili, ma i dati sono essenziali.",
      icon: TrendingUp
    },
    {
      title: "Strategia prima, tattica dopo",
      quote: "Non spendo un euro in advertising prima di avere una strategia chiara. Mai.",
      icon: Zap
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEOHead page="risultati" />
      <Navbar />
      
      {/* Hero Section - Full Screen */}
      <section className="relative min-h-screen bg-[#0b114e] overflow-hidden">
        {/* Background Image - Extended on mobile to avoid edge line */}
        <div 
          className="absolute top-0 right-0 h-full w-full lg:w-[55%]"
          style={isMobile ? { left: '-20%', right: 'auto', width: '120%' } : {}}
        >
          <div 
            className="absolute inset-0 bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url('/media/nicola-hero.webp')`,
              backgroundPosition: isMobile ? '-30% top' : 'center top',
            }}
          />
          <div className="absolute inset-0 bg-[#0b114e]/5" />
          {/* Gradient inside image container */}
          <div className={`absolute inset-0 ${
            isMobile 
              ? 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/80 via-40% to-transparent to-70%' 
              : 'bg-gradient-to-r from-[#0b114e] from-0% via-[#0b114e]/70 via-40% to-transparent to-80%'
          }`} />
        </div>

        {/* Content */}
        <div className="relative min-h-screen flex items-center">
          <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="max-w-2xl">
              {/* Badge */}
              <div 
                className={`hidden sm:inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/10 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: '200ms' }}
              >
                <span className="w-2 h-2 bg-[#d4883a] rounded-full animate-pulse" />
                <span className="text-white/70 text-sm tracking-widest">I risultati</span>
              </div>

              {/* Main Heading */}
              <h1 
                className={`text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ fontFamily: 'Georgia, serif', transitionDelay: '400ms' }}
              >
                Non prometto.{' '}
                <span className="text-[#d4883a] relative">
                  Dimostro.
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                    <path 
                      d="M0 4C50 4 50 4 100 4C150 4 150 4 200 4" 
                      stroke="#d4883a" 
                      strokeWidth="3"
                      strokeLinecap="round"
                      className={`transition-all duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                      style={{ 
                        strokeDasharray: 200, 
                        strokeDashoffset: isLoaded ? 0 : 200,
                        transitionDelay: '1200ms'
                      }}
                    />
                  </svg>
                </span>
              </h1>

              {/* Subtitle */}
              <p 
                className={`text-white/60 text-base sm:text-lg lg:text-xl mb-8 sm:mb-10 leading-relaxed max-w-xl transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ fontFamily: 'Georgia, serif', transitionDelay: '600ms' }}
              >
                Numeri reali da aziende reali. Ogni risultato è <strong className="text-white">documentato e verificabile</strong>. 
                Chiedi referenze, ti metto in contatto diretto con i miei clienti.
              </p>
              
              {/* Stats row - compact on mobile, spaced on desktop */}
              <div 
                className={`flex items-center gap-2 mb-6 sm:hidden text-white/50 text-xs transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '800ms' }}
              >
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>Documentati</span>
                <span className="text-[#d4883a]">·</span>
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>Verificabili</span>
                <span className="text-[#d4883a]">·</span>
                <CheckCircle className="w-3 h-3 text-[#d4883a] flex-shrink-0" />
                <span>Personalizzati</span>
              </div>
              <div 
                className={`hidden sm:flex sm:flex-row sm:flex-wrap sm:gap-6 mb-10 transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '800ms' }}
              >
                {[
                  { label: 'Risultati documentati', icon: CheckCircle },
                  { label: 'Referenze verificabili', icon: CheckCircle },
                  { label: '100% personalizzato', icon: CheckCircle },
                ].map((item, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-2 text-white/60 text-sm sm:text-base transition-all duration-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${900 + index * 100}ms` }}
                  >
                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#d4883a]" />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div 
                className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: '1000ms' }}
              >
                <button
                  onClick={scrollToContact}
                  data-testid="risultati-cta-button"
                  className="group bg-[#d4883a] hover:bg-[#e09a4a] text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full transition-all flex items-center justify-center gap-2 text-base sm:text-lg hover:scale-105 hover:shadow-lg hover:shadow-[#d4883a]/25"
                >
                  Voglio risultati così
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Quote */}
        <div 
          className={`absolute bottom-8 left-6 lg:left-12 max-w-lg hidden sm:block transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ transitionDelay: '1200ms' }}
        >
          <div className="border-l-2 border-[#d4883a]/50 pl-4">
            <p className="text-white/40 italic text-sm">
              "I numeri non mentono.<br />
              I risultati parlano da soli."
            </p>
          </div>
        </div>
      </section>

      {/* Stats Overview */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <AnimatedSection key={index} delay={index * 100} className="text-center">
                  <div className="w-16 h-16 bg-[#0b114e] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-[#d4883a]" />
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-[#0b114e] mb-1">{stat.value}</p>
                  <p className="text-gray-600 text-sm">{stat.label}</p>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Results */}
      <section className="py-20 lg:py-32 bg-[#f8f9fa]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">In evidenza</p>
            <h2 
              className="text-3xl lg:text-5xl text-[#0b114e] mb-6"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Risultati <span className="text-[#d4883a]">concreti</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Questi sono alcuni dei risultati ottenuti con i miei clienti negli ultimi 24 mesi.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {results.map((result, index) => (
              <AnimatedSection key={result.id || index} delay={index * 150}>
                <div 
                  className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border-b-4 border-[#d4883a] h-full"
                >
                  <p className="text-5xl md:text-6xl font-bold text-[#d4883a] mb-4">{result.metric}</p>
                  <h3 className="text-xl font-bold text-[#0b114e] mb-3" style={{ fontFamily: 'Georgia, serif' }}>{result.title}</h3>
                  <p className="text-gray-600">{result.description || result.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">Case studies</p>
            <h2 
              className="text-3xl lg:text-5xl text-[#0b114e]"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Storie di <span className="text-[#d4883a]">successo</span>
            </h2>
          </AnimatedSection>
          
          <div className="space-y-12">
            {caseStudies.map((case_, index) => (
              <AnimatedSection key={index} delay={index * 150}>
                <div className={`rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-500 ${index % 2 === 0 ? 'bg-[#f8f9fa]' : 'bg-white border border-gray-100'}`}>
                  <div className={`grid lg:grid-cols-2 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                    <div className={`p-8 lg:p-12 ${index % 2 !== 0 ? 'lg:order-2' : ''}`}>
                      <span className="inline-block bg-[#d4883a]/10 text-[#d4883a] text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
                        {case_.category}
                      </span>
                      <h3 
                        className="text-2xl md:text-3xl font-bold text-[#0b114e] mb-6"
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {case_.title}
                      </h3>
                      
                      <div className="space-y-6 mb-6">
                        <div>
                          <p className="font-semibold text-[#0b114e] mb-2 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-400" />
                            La sfida
                          </p>
                          <p className="text-gray-600 leading-relaxed">{case_.challenge}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-[#0b114e] mb-2 flex items-center gap-2">
                            <Lightbulb className="w-5 h-5 text-[#d4883a]" />
                            La soluzione
                          </p>
                          <p className="text-gray-600 leading-relaxed">{case_.solution}</p>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <span className="w-4 h-4 rounded-full border-2 border-[#d4883a]" />
                        Timeline: <strong>{case_.timeline}</strong>
                      </p>
                    </div>
                    
                    <div className={`bg-[#0b114e] p-8 lg:p-12 flex items-center ${index % 2 !== 0 ? 'lg:order-1' : ''}`}>
                      <div className="w-full">
                        <p className="text-[#d4883a] text-sm font-semibold mb-8 tracking-widest">Risultati ottenuti</p>
                        <div className="grid grid-cols-2 gap-8">
                          {case_.results.map((result, i) => (
                            <div key={i} className="text-center">
                              <p className="text-3xl md:text-4xl font-bold text-[#d4883a] mb-1">{result.value}</p>
                              <p className="text-white/70 text-sm">{result.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-[#0b114e] relative overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative">
          <AnimatedSection className="text-center mb-12">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">Testimonianze</p>
            <h2 
              className="text-3xl lg:text-5xl text-white mb-4"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              Cosa dicono i <span className="text-[#d4883a]">clienti</span>
            </h2>
          </AnimatedSection>
          
          <TestimonialWall testimonials={testimonials} variant="dark" />
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#d4883a] font-semibold mb-4 tracking-widest text-sm">La filosofia</p>
            <h2 
              className="text-3xl lg:text-5xl text-[#0b114e]"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              I principi dietro i <span className="text-[#d4883a]">risultati</span>
            </h2>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {philosophy.map((item, index) => {
              const Icon = item.icon;
              return (
                <AnimatedSection key={index} delay={index * 150}>
                  <div className="text-center group">
                    <div className="inline-flex mb-6">
                      <div className="w-16 h-16 bg-[#0b114e] rounded-2xl flex items-center justify-center shadow-md">
                        <Icon className="w-7 h-7 text-[#d4883a]" />
                      </div>
                    </div>
                    <h3 
                      className="text-xl font-bold text-[#0b114e] mb-4"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-gray-600 italic leading-relaxed">"{item.quote}"</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#f8f9fa] to-transparent" />
        <div className="absolute top-1/4 right-10 w-32 h-32 border-2 border-[#d4883a]/20 rounded-full animate-spin" style={{ animationDuration: '20s' }} />
        
        <AnimatedSection className="max-w-[800px] mx-auto px-6 lg:px-12 text-center relative">
          <h2 
            className="text-3xl lg:text-5xl text-[#0b114e] mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            Il prossimo successo potrebbe essere il <span className="text-[#d4883a]">tuo</span>
          </h2>
          <p className="text-gray-600 text-lg lg:text-xl mb-10 max-w-2xl mx-auto">
            Prenota una consulenza gratuita e scopri come posso aiutare 
            anche la tua azienda a raggiungere risultati straordinari.
          </p>
          <button
            onClick={scrollToContact}
            data-testid="risultati-cta-final-button"
            className="group inline-flex items-center gap-3 bg-[#d4883a] hover:bg-[#e09a4a] text-white font-semibold px-10 py-5 rounded-full transition-all text-lg hover:scale-105 hover:shadow-lg hover:shadow-[#d4883a]/25"
          >
            Prenota la tua consulenza gratuita
            <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
          </button>
        </AnimatedSection>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default RisultatiPage;
